<template>
  <GhostComponent
    :title="title"
    component-name-when-active="PopperCardButton"
    global
    slot-to-render-when-inactive="btn"
    placement="right"
    strategy="absolute"
    style="display: inline-block"
  >
    <template #btn>
      <BaseLabel
        basic
        class="m-r-5 f-11"
        :text="`#${billNumber}`"
        :data-test="`bill-popover-trigger-${billNumber}`"
        @click="readyToLoadData = true"
      />
    </template>
    <template #popper>
      <div v-if="$apollo.loading" class="text-center loader">
        <div class="preloader m-20 m-b-0 pls-blue pls-center">
          <svg class="pl-circular" viewBox="25 25 50 50">
            <circle class="plc-path" cx="50" cy="50" r="20"></circle>
          </svg>
        </div>
      </div>
      <template v-if="loaded">
        <template v-if="moment(bill.startDate)?.isValid() && moment(bill.endDate)?.isValid()">
          <div class="row">
            <div class="col-sm-6 nowrap">
              <strong>{{ $t('Freelancer') }}:</strong>
            </div>
            <div class="col-sm-6">
              <span v-if="bill.worker.profile === null">{{ bill.worker.name }}</span>
              <a v-else :href="'/profile/' + bill.worker.profile.id">
                {{ bill.worker.name }}
              </a>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-6 nowrap">
              <strong>{{ $t('Job ID') }}:</strong>
            </div>
            <div class="col-sm-6">
              {{ bill.job.number }}
            </div>
          </div>
          <div class="row">
            <div class="col-sm-6 nowrap">
              <strong>{{ $t('Hire ID') }}:</strong>
            </div>
            <div class="col-sm-6">
              {{ bill.hire.number }}
            </div>
          </div>
          <div class="row">
            <div class="col-sm-6 nowrap">
              <strong>{{ $t('Date from') }}:</strong>
            </div>
            <div class="col-sm-6 nowrap">
              {{ moment(bill.startDate).format('DD. MMM, Y') }}
            </div>
          </div>
          <div class="row">
            <div class="col-sm-6 nowrap">
              <strong>{{ $t('Date to') }}:</strong>
            </div>
            <div class="col-sm-6 nowrap">
              {{ moment(bill.endDate).format('DD. MMM, Y') }}
            </div>
          </div>
        </template>
        <template v-if="useTimesheetValues">
          <div class="row">
            <div class="col-sm-6 nowrap">
              <strong>
                <template v-if="isHourly">
                  {{ hasOvertimeHours > 0 ? $t('Regular hours') : $t('Hours') }}
                </template>
                <template v-else>{{ rateTypeTitle }}</template>
              </strong>
            </div>
            <div class="col-sm-6">
              <template v-if="isHourly">
                {{ totalPayableTimeMinusOvertime || '0' }}
              </template>
              <template v-else>{{ bill.rateQuantity }}</template>
            </div>
          </div>
          <div v-for="overtime in overtimes" :key="overtime.rate_multiplier" class="row tw-mt-1">
            <div class="col-sm-6">
              {{ $t('Overtime') }}
              <span v-if="!isHourly">{{ $t('hours') }}</span>
              <WdsTooltip
                v-if="overtime.contributingPaymentRequests.size > 1"
                :key="overtime.rate_multiplier"
                right
                class="ml-1"
              >
                <i class="zmdi zmdi-help" />
                <template #tooltip-content>
                  {{
                    $t(
                      'This overtime has been calculated from time submitted across multiple payment requests:',
                    )
                  }}
                  <span
                    v-for="contributingPaymentRequest in overtime.contributingPaymentRequests"
                    :key="contributingPaymentRequest"
                  >
                    #{{ contributingPaymentRequest }}
                  </span>
                </template>
              </WdsTooltip>
              <br />
              <WdsTag sm>{{ formatFixedDecimal(overtime.rate_multiplier) }} x rate</WdsTag>
            </div>
            <div class="col-sm-6">
              {{ overtime.duration }}
            </div>
          </div>
        </template>
        <div v-if="showRate" class="row" :class="{ 'tw-mt-2': !!overtimes.length }">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t('Rate') }}:</strong>
          </div>
          <div class="col-sm-6">
            {{ hasMilestone ? $t('Milestone') : bill.rate }}
          </div>
        </div>
        <hr v-if="showRate" class="m-t-10 m-b-5" />
        <div v-if="hasMilestone" class="row" data-test="milestone-details">
          <div class="tw-ml-4 tw-flex tw-flex-col">
            <strong>{{ $t('Milestone details') }}:</strong>
            <div>{{ $t('Name') }}: {{ milestone.name }}</div>
            <div v-if="milestone.purchaseOrderNumber">
              {{ $t('PO number') }}: {{ milestone.purchaseOrderNumber }}
            </div>
            <div>{{ $t('Due date') }}: {{ milestone.details.dueDate }}</div>
          </div>
        </div>
        <hr v-if="hasMilestone" class="m-t-10 m-b-5" />
        <div class="row">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t('Bill amount') }}:</strong>
          </div>
          <div class="col-sm-6">{{ bill.currency }}&nbsp;{{ bill.billedAmount.toFixed(2) }}</div>
        </div>
        <div class="row">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t('Service fee') }}:</strong>
          </div>
          <div class="col-sm-6">{{ bill.currency }}&nbsp;{{ bill.serviceAmount.toFixed(2) }}</div>
        </div>
        <div v-if="bill.expensesAmount > 0" class="row">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t('Expense report(s)') }}:</strong>
          </div>
          <div class="col-sm-6">{{ bill.currency }}&nbsp;{{ bill.expensesAmount.toFixed(2) }}</div>
        </div>
        <div v-if="bill.recruiter" class="row">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t('Recruiter fee') }}:</strong>
          </div>
          <div class="col-sm-6">
            {{ bill.currency }}&nbsp;{{ bill.recruiterFeeAmount.toFixed(2) }}
          </div>
        </div>
        <div v-if="bill.employmentCostAmount > 0" class="row">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t('Employment cost') }}:</strong>
          </div>
          <div class="col-sm-6">
            {{ bill.currency }}&nbsp;{{ bill.employmentCostAmount.toFixed(2) }}
          </div>
        </div>
        <div class="row c-indigo">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t('Total invoice amount') }}:</strong>
          </div>
          <div class="col-sm-6 f-500">{{ bill.currency }}&nbsp;{{ bill.netAmount.toFixed(2) }}</div>
        </div>
        <hr class="m-t-10 m-b-5" />
        <div class="row">
          <div class="col-sm-6 nowrap">
            <strong>{{ $t(`Total incl. ${vatTerminology}`) }}:</strong>
          </div>
          <div class="col-sm-6">{{ bill.currency }}&nbsp;{{ bill.grossAmount.toFixed(2) }}</div>
        </div>

        <div
          v-if="bill.message?.body"
          class="bgm-lightgray p-5 m-t-10 f-12"
          style="max-height: 65px; overflow: auto"
        >
          {{ bill.message.body }}
        </div>
        <a
          :href="'/bills/' + bill.number + '/pdf'"
          :title="$t('Download bill as PDF')"
          target="_blank"
          class="iblock m-t-10"
        >
          <span class="glyphicon glyphicon-file" aria-hidden="true" />
          {{ $t('Download bill') }}
        </a>
      </template>
    </template>
  </GhostComponent>
</template>

<script>
import gql from 'graphql-tag'
import moment from 'moment'
import { WdsTag, WdsTooltip } from '@worksome/wds-components'
import GhostComponent from '@/components/GhostComponent.vue'
import BaseLabel from '@/components/BaseLabel.vue'
import formatFixedDecimal from '@/components/Timesheet/utilities/formatFixedDecimal'
import { RateType } from '@/enums'

export default {
  apollo: {
    $client: 'publicApi',
    bill: {
      skip() {
        return !this.readyToLoadData
      },
      query: gql`
        query PaymentRequest($id: ID!, $accountType: AccountType!) {
          paymentRequest(id: $id) {
            id
            worker {
              id
              name
              profile {
                id
              }
            }
            recruiter {
              id
            }
            job {
              id
              number
            }
            hire {
              id
              number
            }
            number
            startDate
            endDate
            currency
            rate
            rateType
            rateTypeName
            ratetypeNameSlash
            rateQuantity
            billedAmount
            serviceAmount(accountType: $accountType)
            expensesAmount
            recruiterFeeAmount
            employmentCostAmount
            netAmount(accountType: $accountType)
            grossAmount(accountType: $accountType)
            message {
              id
              body
            }
            timesheet {
              id
              overtimes {
                data {
                  id
                  duration
                  rate
                  rateMultiplier
                  contributingPaymentRequestNumbers
                }
              }
            }
            milestones(first: 100) {
              data {
                name
                purchaseOrderNumber
                details {
                  amount
                  dueDate
                }
              }
            }
          }
        }
      `,
      update(data) {
        this.loaded = true
        return data?.paymentRequest
      },
      variables() {
        return {
          id: this.globalId,
          accountType: this.account.type.toUpperCase(),
        }
      },
    },
  },
  components: { WdsTooltip, WdsTag, BaseLabel, GhostComponent },
  props: {
    globalId: {
      type: String,
    },
    billNumber: {
      type: String,
    },
  },
  data() {
    return {
      readyToLoadData: false,
      loaded: false,
      bill: {},
    }
  },
  computed: {
    isHourly() {
      return this.bill.rateType === RateType.HOURLY
    },
    rateTypeTitle() {
      switch (this.bill.rateType.toLowerCase()) {
        case RateType.DAILY:
          return $t('Days')
        case RateType.HOURLY:
          return $t('Hours')
        case RateType.WEEKLY:
          return $t('Weeks')
        default:
          return $t('Rate')
      }
    },
    title() {
      return `${$t('Bill')} ${this.bill?.number || ''}`
    },
    moment: () => moment,
    vatTerminology() {
      return vatTerminology
    },
    useTimesheetValues() {
      return !!this.bill.timesheet?.id
    },
    showRate() {
      return this.bill.rate > 0 && (this.bill.rateType !== 'FIXED' || this.hasMilestone)
    },
    overtimes() {
      const overtimeTotals = []
      const overtimes = this.bill.timesheet?.overtimes?.data || []
      const multipliers = [...new Set(overtimes.map((overtime) => overtime.rateMultiplier))]
      multipliers.forEach((multiplier) => {
        const duration = overtimes
          .filter((overtime) => overtime.rateMultiplier === multiplier)
          .reduce((accumulator, currentOvertime) => accumulator + currentOvertime.duration, 0)
        const total = overtimes
          .filter((overtime) => overtime.rateMultiplier === multiplier)
          .reduce((accumulator, currentOvertime) => accumulator + currentOvertime.total, 0)
        const contributingPaymentRequests = new Set()
        overtimes.forEach((overtime) => {
          if (overtime.rateMultiplier === multiplier) {
            overtime.contributingPaymentRequestNumbers.forEach((paymentRequestId) =>
              contributingPaymentRequests.add(paymentRequestId),
            )
          }
        })
        overtimeTotals.push({
          rate_multiplier: multiplier,
          duration,
          total,
          contributingPaymentRequests,
        })
      })
      return overtimeTotals
    },
    hasMilestone() {
      return !!this.bill.milestones.data.length
    },
    milestone() {
      if (this.hasMilestone) {
        return this.bill.milestones.data[0]
      }
      return null
    },
    hasOvertimeHours() {
      return !!this.overtimes.length
    },
    totalPayableTimeMinusOvertime() {
      const overtimeDuration = this.overtimes.reduce(
        (accumulator, currentOvertime) => accumulator + currentOvertime.duration,
        0,
      )
      // Overtime could be built up based on other timesheets which surpasses the amount of this timesheet
      return Math.max(0, (this.bill.rateQuantity || 0) - overtimeDuration)
    },
  },
  methods: {
    formatFixedDecimal,
  },
}
</script>

<style scoped></style>
